import axios from 'axios';
import config from '../config';

// 本番環境かどうかを判定する
const isProduction = process.env.NODE_ENV === 'production';

// 危険なヘッダーの設定を回避するためのヘルパー関数
const removeForbiddenHeaders = (headers = {}) => {
  const cleanHeaders = {...headers};
  
  // ブラウザが禁止するヘッダー一覧
  const forbiddenHeaders = [
    'content-length',
    'Content-Length',
    'host',
    'Host',
    'connection',
    'Connection',
    'origin', 
    'Origin',
    'referer',
    'Referer',
    'user-agent',
    'User-Agent'
  ];
  
  // すべての禁止ヘッダーを削除
  forbiddenHeaders.forEach(header => {
    delete cleanHeaders[header];
  });
  
  return cleanHeaders;
};

// イベントバスの作成（AuthContextとの連携用）
export const authEvents = {
  // イベントリスナー
  listeners: {
    tokenRefresh: [],
    authError: [],
    apiError: [],     // APIエラーイベント
    networkError: []  // ネットワークエラーイベント
  },
  
  // リスナー登録
  on: function(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event].push(callback);
    }
  },
  
  // イベント発火
  emit: function(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(callback => callback(data));
    }
  }
};

// リクエストのキャッシュとデバウンス用のマップ
const requestCache = new Map();
const pendingRequests = new Map();
// バッチリクエスト用のキュー
const batchRequestQueues = new Map();
// 進行中のリクエストを追跡するオブジェクト
const inFlightRequests = {};
// リソース別のキャッシュ設定
const CACHE_TTL = {
  default: 10000, // 10秒（デフォルト）
  '/api/posts': 30000, // 30秒
  '/api/users': 60000, // 1分
  '/api/settings': 300000, // 5分
  '/api/users/me': 3600000, // 1時間（ログインユーザー情報）
  '/api/messages': 10000, // 10秒（メッセージ）
  '/api/messages/conversations': 10000 // 10秒（会話リスト）
};

// 重複リクエスト対策の設定
const REQUEST_DEBOUNCE = {
  '/api/posts': 5000,      // 投稿一覧は5秒
  '/api/users': 5000,      // ユーザー一覧は5秒
  '/api/notifications': 5000, // 通知は5秒
  'default': 1000          // その他は1秒
};

// APIリクエストごとに認証情報を送信するための追加設定
axios.defaults.withCredentials = false;  // PHP-FPM環境対応

// API設定
// Firebase Hosting rewrite で /api/** → Cloud Run にルーティング
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// Axiosインスタンスの作成
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // モバイル用に30秒に延長
  withCredentials: false,  // PHP-FPM環境対応
  headers: {
    'Accept': 'application/json',
    // Content-Typeはリクエスト時に動的に設定
  }
});

// リクエストインターセプター
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // POSTリクエストの場合のみContent-Typeを設定（プリフライト回避）
    if (config.method === 'post' || config.method === 'put') {
      config.headers['Content-Type'] = 'application/json';
    }
    
    // モバイル用の追加設定
    config.headers['Cache-Control'] = 'no-cache';
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// リクエストからタイムスタンプパラメータをフィルタリングするヘルパー関数
const removeTimestampParam = (params) => {
  // _tパラメータを削除
  if (params && typeof params === 'object') {
    const filteredParams = { ...params };
    delete filteredParams._t;
    return filteredParams;
  }
  return params;
};

// APIサービスのインスタンスを作成（APIサブドメイン用）
const apiClientInstance = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 30000, // モバイル環境を考慮してタイムアウトを延長
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    // モバイルブラウザ対応
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  }
});

// リクエスト前の処理
apiClientInstance.interceptors.request.use(
  (config) => {
    // 認証ヘッダーが必要なリクエストには自動的にトークンを追加
    // authServiceと同じキーを使用
    const token = localStorage.getItem('authToken');
    
    // トークンがある場合は確実に設定
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('【デバッグ】api.js リクエストにトークン追加:', {
        url: config.url,
        method: config.method?.toUpperCase(),
        hasToken: !!token,
        tokenStart: token.substring(0, 20) + '...'
      });
    } else {
      // トークンがない場合のリクエスト - 詳細なlocalStorage状態を確認
      console.log('【デバッグ】api.js トークンなし - リクエスト:', {
        url: config.url,
        method: config.method?.toUpperCase(),
        authToken: localStorage.getItem('authToken'),
        access_token: localStorage.getItem('access_token'),
        allTokenKeys: Object.keys(localStorage).filter(key => key.includes('token')),
        localStorageLength: localStorage.length
      });
      
      // 認証が必要なエンドポイントの場合は警告を出力
      const authRequiredEndpoints = [
        '/api/users/me',
        '/api/notifications/',
        '/api/posts/',
        '/api/comments/',
        '/api/messages/'
      ];
      
      const requiresAuth = authRequiredEndpoints.some(endpoint => 
        config.url && config.url.includes(endpoint)
      );
      
      if (requiresAuth) {
        console.warn('⚠️ [AUTH WARNING] 認証が必要なエンドポイントにトークンなしでリクエスト:', {
          url: config.url,
          method: config.method?.toUpperCase(),
          localStorageCheck: Object.keys(localStorage).filter(key => key.includes('token')),
          timestamp: new Date().toISOString()
        });
      }
    }
    
    // GETリクエストの詳細ログ（特に/api/notifications用）
    if (config.method?.toUpperCase() === 'GET' && config.url?.includes('/api/notifications')) {
      console.log('🔍 [GET REQUEST] GETリクエスト送信準備:', {
        url: config.url,
        method: config.method,
        hasAuth: !!config.headers['Authorization'],
        authHeader: config.headers['Authorization'] ? config.headers['Authorization'].substring(0, 30) + '...' : 'none',
        params: config.params,
        timeout: config.timeout
      });
    }
    
    // POSTリクエストの詳細ログ
    if (config.method?.toUpperCase() === 'POST') {
      console.log('🔵 [POST REQUEST] POSTリクエスト送信準備:', {
        url: config.url,
        method: config.method,
        hasAuth: !!config.headers['Authorization'],
        contentType: config.headers['Content-Type'],
        dataType: typeof config.data,
        dataLength: config.data ? JSON.stringify(config.data).length : 0
      });
    }
    
    // Content-Typeがmultipart/form-dataの場合は設定を変更しない
    if (config.headers['Content-Type'] && config.headers['Content-Type'].includes('multipart/form-data')) {
      // AxiosがContent-Typeヘッダーを自動設定するようにする
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    console.error('🔴 [REQUEST ERROR] リクエスト前エラー:', error);
    return Promise.reject(error);
  }
);

// apiClientInstanceのレスポンスインターセプター
apiClientInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 401エラー（認証失敗）の処理
    if (error.response && error.response.status === 401) {
      console.log('🔐 [401 ERROR] apiClientInstance: 認証エラーを検出');
      
      // 通知関連エンドポイントの401エラーは無視（一時的対応）
      const isNotificationEndpoint = error.config?.url?.includes('/api/notifications/');
      
      if (isNotificationEndpoint) {
        console.warn('⚠️ [401 IGNORED] 通知エンドポイントの401エラーを無視します:', error.config?.url);
        return Promise.reject(error);
      }
      
      console.log('🔐 [401 ERROR] 認証データをクリア - ログイン画面にリダイレクト');
      
      // 認証データをクリア
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('tokenExpiry');
      
      // 認証状態をクリア
      delete apiClientInstance.defaults.headers.common['Authorization'];
      
      // トークン期限切れイベントを発火
      window.dispatchEvent(new CustomEvent('tokenExpired', {
        detail: { reason: 'Token expired - 401 error from apiClientInstance', redirectToLogin: true }
      }));
      
      // 現在のページがログインページでない場合のみリダイレクト
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        // 現在のURLを保存してログイン後にリダイレクト
        sessionStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
        sessionStorage.setItem('session_expired_reason', 'api_401_error');
        
        // ログインページにリダイレクト
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }
    
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // 401エラー（認証失敗）の処理
    if (error.response && error.response.status === 401) {
      console.log('🔐 [401 ERROR] 認証エラーを検出');
      
      // 通知関連エンドポイントの401エラーは無視（一時的対応）
      const isNotificationEndpoint = error.config?.url?.includes('/api/notifications/');
      
      if (isNotificationEndpoint) {
        console.warn('⚠️ [401 IGNORED] 通知エンドポイントの401エラーを無視します:', error.config?.url);
        return Promise.reject(error);
      }
      
      console.log('🔐 [401 ERROR] 認証データをクリア - ログイン画面にリダイレクト');
      
      // 認証データをクリア
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('tokenExpiry');
      
      // 認証状態をクリア
      delete api.defaults.headers.common['Authorization'];
      
      // トークン期限切れイベントを発火
      window.dispatchEvent(new CustomEvent('tokenExpired', {
        detail: { reason: 'Token expired - 401 error', redirectToLogin: true }
      }));
      
      // 現在のページがログインページでない場合のみリダイレクト
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        // 現在のURLを保存してログイン後にリダイレクト
        sessionStorage.setItem('redirect_after_login', window.location.pathname + window.location.search);
        sessionStorage.setItem('session_expired_reason', 'api_401_error');
        
        // ログインページにリダイレクト
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      }
    }
    
    return Promise.reject(error);
  }
);

// 簡易版のトークン管理ユーティリティ
export const tokenManager = {
  getToken() {
    // authServiceと同じキーを使用
    return localStorage.getItem('authToken');
  },
  
  setToken(token) {
    if (token) {
      // authServiceと同じキーを使用
      localStorage.setItem('authToken', token);
    }
  },
  
  clearTokens() {
    // authServiceと同じキーを使用
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('tokenExpiry');
    
          // 古いキーも念のためクリア
    localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
  }
};

// キャッシュクリア関数
export const clearCache = (pattern = null) => {
  if (pattern) {
    // 特定のパターンに一致するキャッシュのみをクリア
    for (const key of requestCache.keys()) {
      if (key.includes(pattern)) {
        requestCache.delete(key);
      }
    }
  } else {
    // すべてのキャッシュをクリア
    requestCache.clear();
  }
};

// セーフAPIコール関数
export const safeApiCall = {
  get: async (url, config = {}) => {
    try {
      const mergedConfig = {
        ...config,
        headers: {
          ...config.headers,
          'Cache-Control': 'no-cache'
        }
      };
      return await apiClientInstance.get(url, mergedConfig);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  
  post: async (url, data = {}, config = {}) => {
    try {
      const mergedConfig = {
        ...config,
        headers: {
          ...config.headers,
          'Cache-Control': 'no-cache'
        }
      };
      return await apiClientInstance.post(url, data, mergedConfig);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  
  put: async (url, data = {}, config = {}) => {
    try {
      const mergedConfig = {
        ...config,
        headers: {
          ...config.headers,
          'Cache-Control': 'no-cache'
        }
      };
      return await apiClientInstance.put(url, data, mergedConfig);
    } catch (error) {
      return Promise.reject(error);
    }
  },

  delete: async (url, config = {}) => {
    try {
      const mergedConfig = {
        ...config,
        headers: {
          ...config.headers,
          'Cache-Control': 'no-cache'
        }
      };
      return await apiClientInstance.delete(url, mergedConfig);
    } catch (error) {
      return Promise.reject(error);
    }
  }
};



// エクスポート用のAPIサービスオブジェクト
export const apiService = {
  // safeApiCallのメソッドを明示的に優先
  get: safeApiCall.get,
  post: safeApiCall.post,
  put: safeApiCall.put,
  delete: safeApiCall.delete,
  // その他のapiClientInstanceのメソッドとプロパティ
  ...apiClientInstance,
  // ユーティリティ関数
  clearCache,
  tokenManager
};

// 削除された投稿IDを保存する関数
export const saveDeletedPostId = (postId) => {
  try {
    if (!postId) return;
    
    // キャッシュの初期化
    if (typeof window !== 'undefined') {
      if (!window.deletedPostsCache) {
        window.deletedPostsCache = new Set();
      }
      
      // IDを追加
      window.deletedPostsCache.add(postId);
      
      // ローカルストレージにも保存
      const deletedPosts = JSON.parse(localStorage.getItem('deletedPosts') || '[]');
      if (!deletedPosts.includes(postId)) {
        deletedPosts.push(postId);
        localStorage.setItem('deletedPosts', JSON.stringify(deletedPosts));
      }
    }
  } catch (error) {
    console.error('削除済み投稿IDの保存に失敗しました:', error);
  }
};

// 削除された投稿のキャッシュをクリアする関数
export const clearDeletedPostCache = (postId) => {
  try {
    if (!postId) return;
    
    // メモリキャッシュから削除
    if (typeof window !== 'undefined' && window.deletedPostsCache) {
      window.deletedPostsCache.delete(postId);
    }
    
    // ローカルストレージからも削除
    try {
      const deletedPosts = JSON.parse(localStorage.getItem('deletedPosts') || '[]');
      const updatedPosts = deletedPosts.filter(id => id !== postId);
      localStorage.setItem('deletedPosts', JSON.stringify(updatedPosts));
    } catch (storageError) {
      console.error('ローカルストレージからの削除に失敗しました:', storageError);
    }
  } catch (error) {
    console.error('削除済み投稿キャッシュのクリアに失敗しました:', error);
  }
};

// プロフィールキャッシュをリフレッシュする関数
export const refreshProfileCache = (username) => {
  try {
    if (!username) return;
    
    // プロフィールキャッシュをクリア
    if (typeof window !== 'undefined' && window.profileDataCache) {
      delete window.profileDataCache[username];
    }
    
    // フォロー状態のキャッシュもクリア
    if (typeof window !== 'undefined' && window.followStateCache) {
      window.followStateCache.delete(username);
    }
  } catch (error) {
    console.error('プロフィールキャッシュのリフレッシュに失敗しました:', error);
  }
};

// ユーザーキャッシュをクリアする関数
export const clearUserCache = (userId) => {
  try {
    if (!userId) return;
    
    // ユーザーキャッシュをクリア（実装予定）
    
  } catch (error) {
    console.error('ユーザーキャッシュのクリアに失敗しました:', error);
  }
};

// 投稿IDが削除済みかどうかを確認する関数
export const isPostIdDeleted = (postId) => {
  try {
    if (!postId) return false;
    
    // メモリキャッシュを確認
    if (typeof window !== 'undefined' && window.deletedPostsCache) {
      if (window.deletedPostsCache.has(postId)) {
        return true;
      }
    }
    
    // ローカルストレージを確認
    try {
      const deletedPosts = JSON.parse(localStorage.getItem('deletedPosts') || '[]');
      return deletedPosts.includes(postId);
    } catch (storageError) {
      console.error('ローカルストレージからの読み込みに失敗しました:', storageError);
      return false;
    }
  } catch (error) {
    console.error('削除済み投稿の確認に失敗しました:', error);
    return false;
  }
};

// 削除済み投稿IDリストから削除する関数
export const removeFromDeletedPostIds = (postId) => {
  try {
    if (!postId) return;
    
    // メモリキャッシュから削除
    if (typeof window !== 'undefined' && window.deletedPostsCache) {
      window.deletedPostsCache.delete(postId);
    }
    
    // ローカルストレージからも削除
    try {
      const deletedPosts = JSON.parse(localStorage.getItem('deletedPosts') || '[]');
      const updatedPosts = deletedPosts.filter(id => id !== postId);
      localStorage.setItem('deletedPosts', JSON.stringify(updatedPosts));
    } catch (storageError) {
      console.error('ローカルストレージからの削除に失敗しました:', storageError);
    }
  } catch (error) {
    console.error('削除済み投稿IDの削除に失敗しました:', error);
  }
};

// 削除済み投稿IDをロードする関数
export const loadDeletedPostIds = () => {
  try {
    // メモリキャッシュの初期化
    if (typeof window !== 'undefined') {
      if (!window.deletedPostsCache) {
        window.deletedPostsCache = new Set();
      }
      
      // ローカルストレージからロード
      try {
        const deletedPosts = JSON.parse(localStorage.getItem('deletedPosts') || '[]');
        
        // メモリキャッシュに追加
        deletedPosts.forEach(id => {
          window.deletedPostsCache.add(id);
        });
        
        return deletedPosts;
      } catch (storageError) {
        console.error('ローカルストレージからのロードに失敗しました:', storageError);
        return [];
      }
    }
    return [];
  } catch (error) {
    console.error('削除済み投稿IDのロードに失敗しました:', error);
    return [];
  }
};

// 画像と動画のURLを正規化するヘルパー関数
export const normalizeImageUrl = (url) => {
  if (!url) return '';
  
  // すでに完全なURLの場合はそのまま返す
  if (url.startsWith('http') || url.startsWith('data:')) {
    return url;
  }
  
  // "/static/"で始まる相対パスの場合、APIのベースURLと結合
  if (url.startsWith('/static/')) {
    const normalizedUrl = `${config.API_BASE_URL}${url}`;
    return normalizedUrl;
  }
  
  // /からはじまる場合もAPIのベースURLと結合
  if (url.startsWith('/')) {
    const normalizedUrl = `${config.API_BASE_URL}${url}`;
    return normalizedUrl;
  }
  
  // それ以外の場合はそのまま返す
  return url;
};

/**
 * APIユーティリティ関数
 * バックエンドPythonスクリプトとの通信を管理します
 */

/**
 * バックエンドAPIを呼び出す汎用関数
 * @param {string} endpoint - 呼び出すエンドポイント名（例: 'users/me'）
 * @param {string} method - HTTPメソッド（GET, POST, PUT, DELETE）
 * @param {object|null} data - リクエストボディに含めるデータ
 * @param {object} additionalHeaders - 追加のHTTPヘッダー
 * @returns {Promise<any>} - レスポンスデータ
 */
export const callApi = async (endpoint, method = 'GET', data = null, additionalHeaders = {}) => {
  const options = {
    method,
    mode: 'cors',
    credentials: 'omit',  // withCredentials: false相当
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...additionalHeaders,
    },
  };

  // 認証トークンがある場合はヘッダーに追加
  const token = localStorage.getItem('authToken');
  if (token) {
    options.headers['Authorization'] = `Bearer ${token}`;
  }

  // POSTまたはPUTリクエストの場合、データをリクエストボディに追加
  if (data && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(data);
  }

  try {
    // APIサブドメインを使用
    const url = `${config.API_BASE_URL}${endpoint}`;
    console.debug(`API request to: ${url}`, { method, data });
    
    const response = await fetch(url, options);
    
    // レスポンスのステータスコードをチェック
    if (!response.ok) {
      let errorMessage = `API error: ${response.status} ${response.statusText}`;
      let errorData = {};
      
      try {
        errorData = await response.json();
        if (errorData && errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        console.warn('レスポンスJSONのパースに失敗しました:', parseError);
      }
      
      console.error(`API error (${response.status}):`, errorMessage, {url, method, data});
      
      // 401エラー（認証切れ）の場合：一気にクリア
      if (response.status === 401) {
        console.log('🔒 認証エラー - 即座にクリア');
        
        // 全認証情報を即座にクリア
        ['authToken', 'refreshToken', 'currentUser', 'tokenExpiry'].forEach(key => 
          localStorage.removeItem(key)
        );
        sessionStorage.clear();
        
        // tokenExpiredイベント送信（AuthContextと同期）
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('tokenExpired', {
            detail: { reason: 'API_401_ERROR', endpoint }
          }));
          
          // ログインページに即座にリダイレクト
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      }
      
      // より詳細なエラー情報を含むErrorオブジェクトを作成
      const error = new Error(errorMessage || `API error: ${response.status}`);
      error.response = {
        status: response.status,
        data: errorData
      };
      error.request = {
        url,
        method,
        data
      };
      
      throw error;
    }
    
    // レスポンスがJSONかどうかを確認
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      // JSONレスポンスをパース
      const responseData = await response.json();
      return responseData;
    } else {
      // JSONでないレスポンスの場合はテキストとして返す
      const textResponse = await response.text();
      console.warn('JSONではないレスポンスを受信しました:', {
        contentType,
        textResponse: textResponse.substring(0, 100) + (textResponse.length > 100 ? '...' : '')
      });
      
      // 空のJSONオブジェクトを返す
      return {};
    }
  } catch (error) {
    const requestUrl = `${config.API_BASE_URL}${endpoint}`;
    console.error('API call failed:', error.message, {url: requestUrl, method, data});
    
    // エラーオブジェクトに詳細情報が含まれていない場合は追加
    if (!error.response && !error.request) {
      error.request = {
        url: requestUrl,
        method,
        data
      };
    }
    
    throw error;
  }
};

/**
 * FormDataを使用したAPI呼び出し用の関数
 * @param {string} endpoint - 呼び出すエンドポイント
 * @param {FormData} formData - 送信するFormDataオブジェクト
 * @returns {Promise<any>} - レスポンスデータ
 */
export const callFormDataApi = async (endpoint, formData) => {
  const options = {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',  // withCredentials: false相当
    headers: {},
    body: formData, // FormDataをそのまま使用
  };

  // 認証トークンがある場合はヘッダーに追加
  const token = localStorage.getItem('authToken');
  if (token) {
    options.headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    // APIサブドメインを使用
    const url = `${config.API_BASE_URL}${endpoint}`;
    console.debug(`FormData API request to: ${url}`);
    
    const response = await fetch(url, options);
    
    // レスポンスのステータスコードをチェック
    if (!response.ok) {
      let errorMessage = `API error: ${response.status} ${response.statusText}`;
      let errorData = {};
      
      try {
        errorData = await response.json();
        if (errorData && errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData && errorData.error) {
          errorMessage = errorData.error;
        }
      } catch (parseError) {
        console.warn('レスポンスJSONのパースに失敗しました:', parseError);
      }
      
      // 404エラーの場合は、詳細なエラーメッセージをログに出力
      if (response.status === 404) {
        console.error(`API endpoint not found: ${url}`, {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(`API endpoint not found: ${endpoint} (404)`);
      }
      
      // その他のエラー
      console.error(`API error (${response.status}):`, errorMessage, {url});
      throw new Error(errorMessage || `API error: ${response.status}`);
    }
    
    // レスポンスがJSONかどうかを確認
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      // JSONレスポンスをパース
      const responseData = await response.json();
      return responseData;
    } else {
      // JSONでないレスポンスの場合はテキストとして返す
      const textResponse = await response.text();
      console.warn('JSONではないレスポンスを受信しました:', {
        contentType,
        textResponse: textResponse.substring(0, 100) + (textResponse.length > 100 ? '...' : '')
      });
      
      // 空のJSONオブジェクトを返す
      return {};
    }
  } catch (error) {
    const requestUrl = `${config.API_BASE_URL}${endpoint}`;
    console.error('FormData API call failed:', error.message, {url: requestUrl});
    throw error;
  }
};

/**
 * ユーザー関連APIを呼び出すユーティリティ関数
 */
export const userApi = {
  // ユーザーログイン
  // 本番では config.API_BASE_URL が空のため、エンドポイントは /api/ から始める必要がある
  // （Firebase Hosting の rewrite が /api/** を Cloud Run に転送する）
  login: (login, password) => 
    callApi('/api/auth/login', 'POST', { login, password }),
  
  // ユーザー登録
  register: (userData) => {
    // FormDataオブジェクトかどうかをチェック
    if (userData instanceof FormData) {
      return callFormDataApi('/api/auth/register', userData);
    }
    return callApi('/api/auth/register', 'POST', userData);
  },
  
  // ユーザープロフィール取得
  getProfile: (userId) => 
    callApi(`/api/users/${userId}`, 'GET'),
  
  // ユーザープロフィール更新
  updateProfile: (profileData) => 
    callApi('/api/users/me', 'PUT', profileData),
    
  // トークンリフレッシュ（一時的に無効化）
  refreshToken: () => {
    console.log('【デバッグ】userApi.refreshToken 一時的に無効化');
    return Promise.reject(new Error('トークンリフレッシュは一時的に無効化されています'));
  },
};

/**
 * 投稿関連APIを呼び出すユーティリティ関数
 */
export const postsApi = {
  // 投稿一覧を取得
  getPosts: (page = 1, limit = 10) => 
    callApi(`posts?page=${page}&limit=${limit}`, 'GET'),
  
  // 投稿詳細を取得
  getPostById: (postId) => 
    callApi(`posts/${postId}`, 'GET'),
  
  // 新規投稿を作成
  createPost: (postData) => 
    callApi('posts', 'POST', postData),
  
  // 投稿を更新
  updatePost: (postId, postData) => 
    callApi(`posts/${postId}`, 'PUT', postData),
  
  // 投稿を削除
  deletePost: (postId) => 
    callApi(`posts/${postId}`, 'DELETE'),
  
  // 投稿にいいねする - フローチャートに基づいた分離実装
  likePost: async (postId) => {
    try {
      // 🔐 1. メインのいいね処理（データベース保存）
      console.log(`🔄 [LIKE] Starting like process for post: ${postId}`);
      const likeResponse = await callApi(`posts/${postId}/like`, 'POST');
      console.log(`✅ [LIKE] Like saved successfully:`, likeResponse);
      
      // 📱 2. プッシュ通知を同期的に送信（プッシュ通知は同期でないと機能しない）
      console.log(`🔍 [DEBUG] Checking notification conditions:`, {
        hasResponse: !!likeResponse,
        notificationNeeded: likeResponse?.notification_needed,
        receiverId: likeResponse?.receiver_id,
        postId: postId
      });
      
      if (likeResponse && likeResponse.notification_needed) {
        console.log(`🔔 [LIKE] Sending push notification for post: ${postId}, receiver: ${likeResponse.receiver_id}`);
        try {
          // プッシュ通知は同期処理で実行
          const pushResponse = await callApi(`notifications/sync-push/like`, 'POST', { 
            post_id: postId,
            receiver_id: likeResponse.receiver_id 
          });
          console.log(`✅ [LIKE] Push notification sent successfully:`, pushResponse);
        } catch (pushError) {
          console.warn(`⚠️ [LIKE] Push notification failed (ignored):`, pushError);
          // プッシュ通知エラーは警告として記録するが、メイン処理は成功として扱う
        }
      } else {
        console.log(`ℹ️ [LIKE] No push notification needed:`, {
          notificationNeeded: likeResponse?.notification_needed,
          receiverId: likeResponse?.receiver_id
        });
      }
      
      return likeResponse;
    } catch (error) {
      console.error(`❌ [LIKE] Like process failed:`, error);
      throw error;
    }
  },
  
  // コメントを追加
  addComment: (postId, content) => 
    callApi(`posts/${postId}/comments`, 'POST', { content }),
};

export default api;

// デバッグAPIを呼び出すための関数
export const callDebugApi = async (endpoint, method = 'GET', data = null, additionalHeaders = {}) => {
  // リクエストのオプションを設定
  const options = {
    method,
    mode: 'cors',
    credentials: 'omit',  // withCredentials: false相当
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      ...additionalHeaders
    },
  };

  // GET以外のリクエストの場合はボディを設定
  if (data && method !== 'GET') {
    options.body = JSON.stringify(data);
  }

  try {
    // デバッグAPIサブドメインを使用
    const url = `${config.DEBUG_API_BASE_URL}${endpoint}`;
    console.debug(`Debug API request to: ${url}`, { method, data });
    
    const response = await fetch(url, options);
    
    // レスポンスのステータスコードをチェック
    if (!response.ok) {
      let errorMessage = `Debug API error: ${response.status} ${response.statusText}`;
      let errorData = {};
      
      try {
        errorData = await response.json();
        if (errorData && errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (parseError) {
        console.warn('レスポンスJSONのパースに失敗しました:', parseError);
      }
      
      // 404エラーの場合は、詳細なエラーメッセージをログに出力
      if (response.status === 404) {
        console.error(`Debug API endpoint not found: ${url}`, {
          method,
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(`Debug API endpoint not found: ${endpoint} (404)`);
      }
      
      // その他のエラー
      console.error(`Debug API error (${response.status}):`, errorMessage, {url, method, data});
      throw new Error(errorMessage || `API error: ${response.status}`);
    }
    
    // レスポンスがJSONかどうかを確認
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      // JSONでない場合はテキストとして返す
      return await response.text();
    }
  } catch (error) {
    console.error('Debug API呼び出しエラー:', error.message);
    throw error;
  }
};

// データベーステスト用のヘルパー関数
export const testDatabaseConnection = async () => {
  try {
    return await callDebugApi('database-test');
  } catch (error) {
    console.error('データベース接続テストエラー:', error);
    throw error;
  }
};

// プッシュ通知設定をリセット
export const resetNotificationSettings = async () => {
  try {
    const response = await apiService.post('/api/notifications/reset-settings');
    return response.data;
  } catch (error) {
    console.error('通知設定リセットエラー:', error);
    throw error;
  }
};

// 通知設定のデバッグ情報を取得
export const getNotificationDebugInfo = async () => {
  try {
    const response = await apiService.get('/api/notifications/debug-user-settings');
    return response.data;
  } catch (error) {
    console.error('デバッグ情報取得エラー:', error);
    throw error;
  }
};

// プッシュ通知機能を手動で初期化
export const initializePushNotifications = async () => {
  try {
    const response = await apiService.post('/api/notifications/manual-init');
    return response.data;
  } catch (error) {
    console.error('プッシュ通知初期化エラー:', error);
    throw error;
  }
};

// プッシュ通知のステータスを確認
export const getPushNotificationStatus = async () => {
  try {
    const response = await apiService.get('/api/notifications/debug-push-status');
    return response.data;
  } catch (error) {
    console.error('プッシュ通知ステータス取得エラー:', error);
    throw error;
  }
}; 